import React from 'react';
import { generatePath, useHistory } from 'react-router-dom';
import LoadingButton from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import SupportEmailLink from '@/components/common/SupportEmailLink';
import AuthRoutes from '@/constants/routing/AuthRoutes';
import useCurrentUser from '@/lib/hooks/useCurrentUser';
import useOwnerOrgId from '@/lib/hooks/useOwnerOrgId';
import translate from '@/lib/intl/translate';
import { postBulkImport } from '@/lib/services/bulkImportServices';
import {
  BulkImportUploader,
  type FileUploadItem,
} from '@/modules/FileUploader';
import { showErrorMessage } from '@/utils/ToastFeedback';
import type PostBulkImportResponse from '../../types/PostBulkImportResponse';
import BackToUploadsButton from './BackToUploadsButton';

function showGenericErrorToast() {
  showErrorMessage({
    message: translate('DATA_UPLOADS_FILE_UPLOAD_OTHER'),
  });
}

export default function NewDataUploadPage() {
  const history = useHistory();
  const { user } = useCurrentUser();
  const ownerOrgId = useOwnerOrgId();
  const [canUpload, setCanUpload] = React.useState(false);
  const [isUploading, setIsUploading] = React.useState(false);
  const [presignedPost, setPresignedPost] = React.useState('');

  const handleFileUpload = async (e: FileUploadItem[]) => {
    const file = e?.[0];
    if (!file) return;
    const { presignedPost: presigned, uploadId } = file;
    if (!uploadId || !presigned) return;

    setPresignedPost(presigned as string);
    setCanUpload(true);
  };

  const handleFileUploadError = (e: unknown) => {
    if (e instanceof Error) {
      switch (e.message) {
        /**
         * This is a list of user friendly messages. All other messages
         * will use the default case which covers errors from CORS or
         * 403s.
         */
        case translate('DATA_UPLOADS_FILE_UPLOAD_WRONG_TYPE'):
        case translate('DATA_UPLOADS_FILE_UPLOAD_TOO_LARGE'):
        case translate('DATA_UPLOADS_FILE_UPLOAD_PROCESSING_ERROR'):
          showErrorMessage({ message: e.message });
          break;

        default:
          showGenericErrorToast();
          break;
      }
    }
  };

  const handleBulkImportChange = React.useCallback(
    (fileUploadItems: FileUploadItem[]) => {
      if (!fileUploadItems.length) {
        setCanUpload(false);
      }
    },
    [],
  );

  const handleAddBulkImport = React.useCallback(async () => {
    setIsUploading(true);

    try {
      const {
        data: {
          bulkImport: { bulkImportId },
        },
      } = await postBulkImport(
        presignedPost,
        {
          displayName: user.displayName,
          userId: user.userId,
        },
        ownerOrgId,
      );
      history.push(
        generatePath(AuthRoutes.COMPANY_DATA_UPLOADS_MATCH_HEADERS, {
          bulkImportId,
        }),
      );
    } catch (e) {
      setIsUploading(false);
      /**
       * If we get the following message back from the API then we
       * have an existing data upload that is in progress. Show the
       * user a specific message to that effect.
       */
      if (
        (e as PostBulkImportResponse).message ===
        'Cannot create new Bulk Import while active ones exist.'
      ) {
        showErrorMessage({
          message: translate('DATA_UPLOADS_FILE_UPLOAD_IN_PROGRESS_ERROR'),
        });
      } else {
        showGenericErrorToast();
      }
    }
  }, [presignedPost]);

  return (
    <div className="min-w-full p-4">
      <Alert icon={false} severity="warning" className="md:hidden">
        {translate('DATA_UPLOADS_MOBILE_MESSAGE')}
      </Alert>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="type-h1">{translate('DATA_UPLOADS_NEW_DATA_UPLOAD')}</h2>
        <BackToUploadsButton />
      </div>

      <div className="mb-8 flex flex-col lg:flex-row">
        <div className="flex-1">
          <p className="mb-3 type-body1">
            {translate('DATA_UPLOADS_NEW_DATA_UPLOAD_INSTRUCTIONS_1')}
          </p>

          <h3 className="type-body2">
            {translate('DATA_UPLOADS_NEW_DATA_UPLOAD_INSTRUCTIONS_2')}
          </h3>

          <p className="mb-3 type-body1">
            {translate('DATA_UPLOADS_NEW_DATA_UPLOAD_INSTRUCTIONS_3')}
          </p>
        </div>

        <Divider
          className="mx-6 hidden border-grey-300 lg:block"
          orientation="vertical"
          flexItem
        />

        <div className="flex-1">
          <h3 className="type-body2">
            {translate('DATA_UPLOADS_NEW_DATA_UPLOAD_INSTRUCTIONS_4')}
          </h3>

          <p className="mb-3 type-body1">
            {translate('DATA_UPLOADS_NEW_DATA_UPLOAD_INSTRUCTIONS_5')}
          </p>

          <h3 className="type-body2">
            {translate('DATA_UPLOADS_NEW_DATA_UPLOAD_INSTRUCTIONS_6')}
          </h3>

          <p className="mb-3 type-body1">
            {translate('DATA_UPLOADS_NEW_DATA_UPLOAD_INSTRUCTIONS_7')}
          </p>

          <p className="mb-3 type-body1">
            {translate('DATA_UPLOADS_NEW_DATA_UPLOAD_INSTRUCTIONS_8', {
              emailLink: SupportEmailLink,
            })}
          </p>
        </div>
      </div>

      <BulkImportUploader
        className="mb-6"
        onChange={handleBulkImportChange}
        onError={handleFileUploadError}
        onUploadFinished={handleFileUpload}
      />
      <LoadingButton
        className="m-auto block"
        disabled={!canUpload}
        loading={isUploading}
        onClick={handleAddBulkImport}
        variant="contained"
      >
        {translate('UPLOAD_FILE')}
      </LoadingButton>
    </div>
  );
}
